import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { PerformanceCategory, ThresholdInfo } from './useTakataDealers';

/**
 * Data access for the regional view.
 *
 * One request per wave carrying both levels the screen works at: province
 * aggregates for the choropleth, and the dealer rows behind them for the panel
 * that opens when a province is clicked.
 */

const BASE = '/api/stla/regional';

/** Where the NUTS3 boundary files are served from. */
export const GEOJSON_BASE_PATH = '/mystery/geoJson/regions/';

export interface RegionalProvince {
  nuts3Code: string;
  nuts3Name: string;
  /** ISO code, resolved server-side even when the source row had none. */
  country: string;
  totalDealers: number;
  averageWaitDays: number;
  totalCalls: number;
  totalAnswered: number;
  answerRate: number;
}

export interface DealerWithNuts {
  name: string;
  city: string;
  address: string;
  zipCode: string;
  oic: string;
  physOut: string;
  country: string;
  brand: string;
  nuts3Code: string;
  averageWaitDays: number | null;
  categoryIndex: number | null;
  nuts3Name: string;
  calls: number;
  carType: string;
  answeredCalls: number;
  phone: string;
  answerRate: number;
  hasAppointment: boolean;
}

export interface RegionalDashboard {
  availableWaves: string[];
  selectedWave: string | null;
  provinces: RegionalProvince[];
  dealers: DealerWithNuts[];
  countries: string[];
  performanceCategories: Record<string, PerformanceCategory>;
  thresholdsByCountry: Record<string, ThresholdInfo[]>;
  countryCodes: Record<string, string>;
  countryNames: Record<string, string>;
}

export const regionalKeys = {
  all: ['stla', 'regional'] as const,
  dashboard: (wave: string | null) => ['stla', 'regional', 'dashboard', wave] as const,
};

/** Provinces and dealers for one wave; null wave means the most recent one. */
export function useRegionalDashboard(wave: string | null) {
  return useQuery<RegionalDashboard>({
    queryKey: regionalKeys.dashboard(wave),
    queryFn: () => api.get<RegionalDashboard>(BASE, { query: { wave } }),
    staleTime: 5 * 60 * 1000,
  });
}
